import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import type { FacilityEntity, LocationEntity } from '@peritasai/repository';
import {
  useCreateLocation,
  useUpdateLocation,
  useLocationTypes,
  useFacilities,
  useLocations,
} from '@peritasai/repository';
import { AssetLifecycleFunction } from '@peritasai/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';

export { DrawerMode as LocationDrawerMode };

interface LocationDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedLocation: LocationEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormState {
  facilityItem: FacilityEntity | null;
  facilitySearch: string;
  name: string;
  departmentOrUnit: string;
  floor: string;
  locationTypeId: string;
  assetLifecycleFunction: string;
  dropoff: boolean;
  parentItem: LocationEntity | null;
  parentSearch: string;
  xCoordinate: string;
  yCoordinate: string;
  zCoordinate: string;
  proximityRange: string;
  additionalDetails: string;
}

interface FormErrors {
  facilityItem?: string;
  name?: string;
  departmentOrUnit?: string;
  floor?: string;
  locationTypeId?: string;
  apiError?: string;
}

const emptyForm: FormState = {
  facilityItem: null,
  facilitySearch: '',
  name: '',
  departmentOrUnit: '',
  floor: '',
  locationTypeId: '',
  assetLifecycleFunction: '',
  dropoff: false,
  parentItem: null,
  parentSearch: '',
  xCoordinate: '',
  yCoordinate: '',
  zCoordinate: '',
  proximityRange: '',
  additionalDetails: '',
};

function validate(
  form: FormState,
  t: (key: string, options?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (!form.facilityItem) {
    errors.facilityItem = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.locationDrawer.facilityLabel'),
    });
  }
  if (!form.name.trim()) {
    errors.name = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.locationDrawer.nameLabel'),
    });
  }
  if (!form.departmentOrUnit.trim()) {
    errors.departmentOrUnit = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.locationDrawer.departmentOrUnitLabel'),
    });
  }
  if (!form.floor.trim()) {
    errors.floor = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.locationDrawer.floorLabel'),
    });
  }
  if (!form.locationTypeId) {
    errors.locationTypeId = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.locationDrawer.typeLabel'),
    });
  }
  return errors;
}

export const LocationDrawer = ({
  open,
  mode,
  selectedLocation,
  onClose,
  onSuccess,
}: LocationDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);

  const createMutation = useCreateLocation();
  const updateMutation = useUpdateLocation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { data: locationTypes } = useLocationTypes();
  const { data: facilityData, isLoading: isFacilityLoading } = useFacilities(
    { search: form.facilitySearch, limit: 20 }
  );
  const { data: locationData, isLoading: isLocationLoading } = useLocations(
    { search: form.parentSearch, facilityId: form.facilityItem?.id, limit: 20 },
    !!form.facilityItem && mode !== DrawerMode.Edit
  );

  const locationTypeOptions = locationTypes ?? [];
  const facilityOptions = facilityData?.facilities ?? [];

  const locationOptions = form.facilityItem
    ? (locationData?.data ?? []).filter((loc) => loc.id !== selectedLocation?.id)
    : [];

  const entityName = t('pages.entityManagement.locationDrawer.entityName');

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedLocation) {
      setForm({
        facilityItem: selectedLocation.facilityId
          ? ({ id: selectedLocation.facilityId, name: selectedLocation.facilityName } as unknown as FacilityEntity)
          : null,
        facilitySearch: selectedLocation.facilityName,
        name: selectedLocation.name,
        departmentOrUnit: selectedLocation.departmentOrUnit,
        floor: selectedLocation.floor,
        locationTypeId: selectedLocation.locationTypeId ?? '',
        assetLifecycleFunction: selectedLocation.assetLifecycleFunction ?? '',
        dropoff: selectedLocation.dropoff ?? false,
        parentItem: selectedLocation.parentId
          ? ({ id: selectedLocation.parentId, name: selectedLocation.parentLocationName ?? '' } as unknown as LocationEntity)
          : null,
        parentSearch: selectedLocation.parentLocationName ?? '',
        xCoordinate: selectedLocation.xCoordinate != null ? String(selectedLocation.xCoordinate) : '',
        yCoordinate: selectedLocation.yCoordinate != null ? String(selectedLocation.yCoordinate) : '',
        zCoordinate: selectedLocation.zCoordinate != null ? String(selectedLocation.zCoordinate) : '',
        proximityRange: selectedLocation.proximityRange ?? '',
        additionalDetails: selectedLocation.additionalDetails ?? '',
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedLocation]);

  const handleTextField =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = { ...form, [field]: e.target.value };
      setForm(next);
      if (touched) setErrors(validate(next, t));
    };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const parseCoord = (v: string) => {
      const n = parseFloat(v);
      return isNaN(n) ? undefined : n;
    };

    try {
      if (mode === DrawerMode.Create) {
        await createMutation.mutateAsync({
          facilityId: form.facilityItem?.id ?? '',
          name: form.name.trim(),
          departmentOrUnit: form.departmentOrUnit.trim(),
          floor: form.floor.trim(),
          locationTypeId: form.locationTypeId,
          ...(form.assetLifecycleFunction && {
            assetLifecycleFunction: form.assetLifecycleFunction as AssetLifecycleFunction,
          }),
          dropoff: form.dropoff,
          ...(form.parentItem && { parentId: form.parentItem.id }),
          ...(form.additionalDetails.trim() && { additionalDetails: form.additionalDetails.trim() }),
          ...(form.proximityRange.trim() && { proximityRange: form.proximityRange.trim() }),
          ...(form.xCoordinate && { xCoordinate: parseCoord(form.xCoordinate) }),
          ...(form.yCoordinate && { yCoordinate: parseCoord(form.yCoordinate) }),
          ...(form.zCoordinate && { zCoordinate: parseCoord(form.zCoordinate) }),
        });
        setForm(emptyForm);
        setTouched(false);
        onSuccess?.();
      } else if (mode === DrawerMode.Edit && selectedLocation) {
        await updateMutation.mutateAsync({
          id: selectedLocation.id,
          dto: {
            facilityId: form.facilityItem?.id ?? '',
            name: form.name.trim(),
            departmentOrUnit: form.departmentOrUnit.trim(),
            floor: form.floor.trim(),
            locationTypeId: form.locationTypeId,
            ...(form.assetLifecycleFunction && {
              assetLifecycleFunction: form.assetLifecycleFunction as AssetLifecycleFunction,
            }),
            dropoff: form.dropoff,
            ...(form.parentItem && { parentId: form.parentItem.id }),
            additionalDetails: form.additionalDetails.trim() || undefined,
            proximityRange: form.proximityRange.trim() || undefined,
            xCoordinate: parseCoord(form.xCoordinate),
            yCoordinate: parseCoord(form.yCoordinate),
            zCoordinate: parseCoord(form.zCoordinate),
          },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  return (
    <SideDrawer
      open={open}
      title={
        mode === DrawerMode.Create
          ? t('pages.entityManagement.drawer.titleCreate', { entityName })
          : t('pages.entityManagement.drawer.titleEdit', { entityName })
      }
      onClose={onClose}
      footer={
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={isPending}
          onClick={() => void handleSubmit()}
          sx={{ borderRadius: '24px', height: 52 }}
        >
          {isPending
            ? t('pages.entityManagement.drawer.submitting')
            : t('pages.entityManagement.drawer.submit')}
        </Button>
      }
    >
      <Grid container spacing={2}>
        {/* Facility */}
        <Grid size={12}>
          <Autocomplete
            options={facilityOptions}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => [opt.name, opt.address].filter(Boolean).join(' - ')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={form.facilityItem}
            inputValue={form.facilitySearch}
            loading={isFacilityLoading}
            filterOptions={(x) => x}
            onInputChange={(_, value) => {
              setForm((prev) => ({
                ...prev,
                facilityItem: null,
                facilitySearch: value,
                parentItem: null,
                parentSearch: '',
              }));
            }}
            onChange={(_, value) => {
              const next = {
                ...form,
                facilityItem: value,
                facilitySearch: [value?.name, value?.address].filter(Boolean).join(' - '),
                parentItem: null,
                parentSearch: '',
              };
              setForm(next);
              if (touched) setErrors(validate(next, t));
            }}
            renderInput={({ InputProps, inputProps, InputLabelProps, id, disabled, fullWidth }) => (
              <TextField
                id={id}
                disabled={disabled}
                fullWidth={fullWidth}
                label={`${t('pages.entityManagement.locationDrawer.facilityLabel')} *`}
                size="small"
                error={touched && !!errors.facilityItem}
                helperText={touched ? errors.facilityItem : undefined}
                slotProps={{
                  input: InputProps as object,
                  htmlInput: inputProps,
                  inputLabel: InputLabelProps,
                  formHelperText: { sx: { ml: '5px' } },
                }}
              />
            )}
          />
        </Grid>

        {/* Name + Department or Unit */}
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.locationDrawer.nameLabel')} *`}
            fullWidth
            size="small"
            value={form.name}
            onChange={handleTextField('name')}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.locationDrawer.departmentOrUnitLabel')} *`}
            fullWidth
            size="small"
            value={form.departmentOrUnit}
            onChange={handleTextField('departmentOrUnit')}
            error={touched && !!errors.departmentOrUnit}
            helperText={touched ? errors.departmentOrUnit : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>

        {/* Floor + Type */}
        <Grid size={6}>
          <TextField
            label={`${t('pages.entityManagement.locationDrawer.floorLabel')} *`}
            fullWidth
            size="small"
            value={form.floor}
            onChange={handleTextField('floor')}
            error={touched && !!errors.floor}
            helperText={touched ? errors.floor : undefined}
            slotProps={{ formHelperText: { sx: { ml: '5px' } } }}
          />
        </Grid>
        <Grid size={6}>
          <FormControl
            fullWidth
            size="small"
            error={touched && !!errors.locationTypeId}
          >
            <InputLabel>
              {`${t('pages.entityManagement.locationDrawer.typeLabel')} *`}
            </InputLabel>
            <Select
              value={form.locationTypeId}
              label={`${t('pages.entityManagement.locationDrawer.typeLabel')} *`}
              onChange={(e) => {
                const next = { ...form, locationTypeId: e.target.value };
                setForm(next);
                if (touched) setErrors(validate(next, t));
              }}
            >
              {locationTypeOptions.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.name}
                </MenuItem>
              ))}
            </Select>
            {touched && errors.locationTypeId && (
              <FormHelperText sx={{ ml: '5px' }}>
                {errors.locationTypeId}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>

        {/* Asset Lifecycle Function + Drop Off Point */}
        <Grid size={6}>
          <FormControl fullWidth size="small">
            <InputLabel>
              {t('pages.entityManagement.locationDrawer.assetLifecycleFunctionLabel')}
            </InputLabel>
            <Select
              value={form.assetLifecycleFunction}
              label={t('pages.entityManagement.locationDrawer.assetLifecycleFunctionLabel')}
              onChange={(e) => setForm({ ...form, assetLifecycleFunction: e.target.value })}
            >
              <MenuItem value="">
                <em>{t('common.none')}</em>
              </MenuItem>
              {Object.values(AssetLifecycleFunction).map((fn) => (
                <MenuItem key={fn} value={fn}>
                  {fn}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.dropoff}
                onChange={(e) => setForm({ ...form, dropoff: e.target.checked })}
                color="primary"
              />
            }
            label={t('pages.entityManagement.locationDrawer.dropOffPointLabel')}
          />
        </Grid>

        {/* Parent Location */}
        <Grid size={12}>
          <Autocomplete
            disabled={mode === DrawerMode.Edit}
            options={locationOptions}
            getOptionKey={(opt) => opt.id}
            getOptionLabel={(opt) => [opt.name, opt.departmentOrUnit].filter(Boolean).join(' - ')}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={form.parentItem}
            inputValue={form.parentSearch}
            loading={isLocationLoading}
            filterOptions={(x) => x}
            onInputChange={(_, value) => {
              setForm((prev) => ({ ...prev, parentItem: null, parentSearch: value }));
            }}
            onChange={(_, value) => {
              setForm((prev) => ({
                ...prev,
                parentItem: value,
                parentSearch: [value?.name, value?.departmentOrUnit].filter(Boolean).join(' - '),
              }));
            }}
            renderInput={({ InputProps, inputProps, InputLabelProps, id, disabled, fullWidth }) => (
              <TextField
                id={id}
                disabled={disabled}
                fullWidth={fullWidth}
                label={t('pages.entityManagement.locationDrawer.parentLocationLabel')}
                size="small"
                slotProps={{
                  input: InputProps as object,
                  htmlInput: inputProps,
                  inputLabel: InputLabelProps,
                }}
              />
            )}
          />
        </Grid>

        {/* Coordinates */}
        <Grid size={6}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="X"
              size="small"
              type="number"
              value={form.xCoordinate}
              onChange={handleTextField('xCoordinate')}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Y"
              size="small"
              type="number"
              value={form.yCoordinate}
              onChange={handleTextField('yCoordinate')}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Z"
              size="small"
              type="number"
              value={form.zCoordinate}
              onChange={handleTextField('zCoordinate')}
              sx={{ flex: 1 }}
            />
          </Box>
        </Grid>

        {/* Proximity Range */}
        <Grid size={6}>
          <TextField
            label={t('pages.entityManagement.locationDrawer.proximityRangeLabel')}
            fullWidth
            size="small"
            value={form.proximityRange}
            onChange={handleTextField('proximityRange')}
          />
        </Grid>

        {/* Additional Details */}
        <Grid size={12}>
          <TextField
            label={t('pages.entityManagement.locationDrawer.additionalDetailsLabel')}
            fullWidth
            size="small"
            multiline
            rows={2}
            value={form.additionalDetails}
            onChange={handleTextField('additionalDetails')}
          />
        </Grid>
      </Grid>

      {touched && errors.apiError && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'error.main', mt: 0.5 }}>
            {errors.apiError}
          </Typography>
        </Box>
      )}
    </SideDrawer>
  );
};
